import { AppModule } from 'app/module';
import { app } from 'app/app';
import { userDislike } from 'app/userDislike';
import { userWatchlist } from 'app/userWatchlist';
import { JobsListItem } from 'module/jobsListItem/main';
import { ContentAd } from 'module/contentAd/main';
import { BrandingSolutionAdCard } from 'module/brandingSolutionAdCard/main';
import { HokifyJobsItem } from 'module/hokifyJobsItem/main';
import { AlarmDisruptor } from 'module/alarmDisruptor/main';
import { mediaQueries } from 'util/mediaQueries';
import { trackGa4 } from 'util/googleAnalytics';
import { VISITED_ID_CACHE_KEY } from 'app/storageKeys';
import localforage from 'localforage';
import { JobsListHeader } from 'module/jobsListHeader/main';
import Template from './template.hbs';
import './styles.scss';

const NEW_BADGE_CACHE_DURATION = 1000 * 60 * 60 * 24; // 1 day
const INTERVAL_BETWEEN_NEW_BADGE_FADE_INS = 120;

export class JobsList extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    domBindings() {
        return {
            jobsListItems: ['.m-jobsListItem'],
            activeJobsListItem: '.m-jobsListItem--highlighted',
            contentAd: '.m-jobsList__contentAd',
            bsAdContainer: '.m-jobsList__bsAdContainer .m-brandingSolutionAdCard',
            alarmDisruptor: '.m-jobsList__alarmDisruptor .m-alarmDisruptor',
            alarmDisruptorContainer: '.m-jobsList__alarmDisruptor',
            hokifyJobs: ['.m-hokifyJobsItem'],
            inactiveJobsListHeader: '.m-jobsList__inactiveJobsListHeader .m-jobsListHeader',
        };
    }

    ready() {
        app.events.on('firstUserInteraction', () => this.onFirstUserInteraction());

        this.jobsListItems?.filter((jobsListItem) => jobsListItem.hasNewBadge())
             
            .forEach((jobsListItem, index) => setTimeout(() => jobsListItem.fadeInNewBadge(), INTERVAL_BETWEEN_NEW_BADGE_FADE_INS * (index + 1)));

        if (this.dom.activeJobsListItem) {
            this.scrollToItem();
        }

        let hasPushedJobs = false;

        this.jobsListItems.forEach((item) => {
            if (item.props.pushedJob) hasPushedJobs = true;
        });

        if (hasPushedJobs) {
            trackGa4({
                event: 'show_element',
                element: 'JOBS: Pushed Jobs',
                element_detail: null,
            });
        }
    }

    scrollToItem(item = this.dom.activeJobsListItem) {
        item.scrollIntoView();
    }

    subs() {
        this.jobsListItems = [];
        if (this.dom.jobsListItems) {
            for (let i = 0; i < this.dom.jobsListItems.length; i += 1) {
                this.jobsListItems[i] = new JobsListItem(this.dom.jobsListItems[i]);
            }
        }

        this.hokifyJobsItems = [];
        if (this.dom.hokifyJobs) {
            for (let i = 0; i < this.dom.hokifyJobs.length; i += 1) {
                this.hokifyJobsItems[i] = new HokifyJobsItem(this.dom.hokifyJobs[i]);
            }
        }

        if (this.dom.contentAd) {
            this.contentAd = new ContentAd(this.dom.contentAd);
        }

        if (this.dom.alarmDisruptor) {
            this.alarmDisruptor = new AlarmDisruptor(this.dom.alarmDisruptor);
        }

        if (this.dom.bsAdContainer) {
            this.brandingSolutionAd = new BrandingSolutionAdCard(this.dom.bsAdContainer);
        } else {
            this.brandingSolutionAd = false;
        }

        if (this.dom.inactiveJobsListHeader) {
            this.inactiveJobsListHeader = new JobsListHeader(this.dom.inactiveJobsListHeader);
        }
    }

    subEvents() {
        if (this.dom.el.getAttribute('data-type') === 'watchlist') {
            userWatchlist.events.on('onRemoveJob', (watchlistObject) => {
                this.removeJobsListItem(watchlistObject.arguments[0]);
            });
            userWatchlist.events.on('onAddJob', (watchlistObject) => {
                this.addJobsListItem(watchlistObject.arguments[0]);
            });
        }
        userDislike.events.on('onDislikeJob', (dislikeObject) => {
            this.removeJobsListItem(
                dislikeObject.arguments[0],
                dislikeObject.arguments[1],
                false,
                dislikeObject.arguments[2],
            );
        });
        userDislike.events.on('onDislikeCompany', (dislikeObject) => {
            this.removeJobsListItem(dislikeObject.arguments[0], dislikeObject.arguments[1], dislikeObject.arguments[2]);
            this.events.emit('onDislikeCompany', dislikeObject.arguments[4]);
        });
        userDislike.events.on('onUndoDislike', (undoDislikeObject) => {
            this.addJobsListItem(
                undoDislikeObject.arguments[0],
                undoDislikeObject.arguments[1],
                undoDislikeObject.arguments[2],
            );
            if (undoDislikeObject.arguments[1]) { // Check if it is a company
                this.events.emit('onUndoDislikeCompany', undoDislikeObject.arguments[3]);
            }
        });
        for (let i = 0; i < this.jobsListItems.length; i += 1) {
            this.propagateEvents(this.jobsListItems[i], 'jobClick');
            this.propagateEvents(this.jobsListItems[i], 'openMemoTab');

            this.jobsListItems[i].events.on('jobClick', () => {
                this.onJobClick(this.dom.jobsListItems[i]);
            });
            this.jobsListItems[i].events.on('openMemoTab', () => {
                this.onOpenMemoTab(this.dom.jobsListItems[i]);
            });
        }

        this.propagateEvents(this.alarmDisruptor, 'removeModalStyle');
        this.propagateEvents(this.alarmDisruptor, 'addModalStyle');
        this.propagateEvents(this.alarmDisruptor, 'jobAlarmResponseSuccess');

        this.propagateEvents(this.contentAd, 'openLoginModal');
    }

    async cleanUpJobBadgeCache() {
        let cache = await localforage.getItem(VISITED_ID_CACHE_KEY) ?? [];
        cache = cache.filter((entry) => (new Date().getTime() - entry.date.getTime()) / NEW_BADGE_CACHE_DURATION < 1);
        await localforage.setItem(VISITED_ID_CACHE_KEY, cache);
    }

    async onFirstUserInteraction() {
        this.cleanUpJobBadgeCache();

        const disruptor = this.dom.el.getAttribute('data-disruptor')
            ? JSON.parse(this.dom.el.getAttribute('data-disruptor')) : false;
        const disruptorModules = {
            contentAd: ContentAd,
            alarmDisruptor: AlarmDisruptor,
        };
        if (disruptor) {
            Object.keys(disruptor).forEach((key) => {
                if (disruptor[key].active) {
                    const listItem = document.createElement('li');
                    const div = document.createElement('div');
                    listItem.className = `m-jobsList__item m-jobsList__${key}`;
                    div.className = key;
                    listItem.appendChild(div);
                    this.dom.el.insertBefore(listItem, this.dom.el.children[disruptor[key].index]);
                    this[key] = new disruptorModules[key](div);
                    this[key].setProps(disruptor[key], true);
                    const itemBeforeAd = this.dom.el.querySelector(`.m-jobsList__item--${key}`);
                    if (itemBeforeAd) {
                        itemBeforeAd.classList.remove(`m-jobsList__item--${key}`);
                    }
                }
            });
        }

        this.propagateEvents(this.alarmDisruptor, 'removeModalStyle');
        this.propagateEvents(this.alarmDisruptor, 'addModalStyle');
        this.propagateEvents(this.alarmDisruptor, 'jobAlarmResponseSuccess');

        this.propagateEvents(this.contentAd, 'openLoginModal');
    }

    removeHighlightedJobItems() {
        for (let j = 0; j < this.jobsListItems.length; j += 1) {
            this.dom.jobsListItems[j].classList.remove('m-jobsListItem--highlighted');
        }
    }

    highlightJobItem(selectedJobId, onReady) {
        for (let i = 0; i < this.jobsListItems.length; i += 1) {
            if (this.jobsListItems[i].props.id === selectedJobId) {
                this.dom.jobsListItems[i].classList.add('m-jobsListItem--highlighted');
                if (onReady) this.scrollToItem(this.dom.jobsListItems[i]);
            }
        }
    }

    insideListing(selectedJobId, companyName = false) {
        for (let i = 0; i < this.jobsListItems.length; i += 1) {
            if (this.jobsListItems[i].props.id === selectedJobId
                || (companyName && this.jobsListItems[i].props.companyId === selectedJobId)) {
                return true;
            }
        }
        return false;
    }

    insideListingWatched(selectedJobId) {
        for (let i = 0; i < this.jobsListItems.length; i += 1) {
            if (this.jobsListItems[i].props.id === selectedJobId) {
                return this.jobsListItems[i].watchlistButton.props.isWatched;
            }
        }
        return false;
    }

    onJobalarmResponseSuccess(response) {
        if (this.alarmDisruptor) {
            this.alarmDisruptor.onResponse(response, true);
        }
    }

    onJobAlarmRemoveResponse(response) {
        if (this.alarmDisruptor) {
            this.alarmDisruptor.onRemoveResponse(response, true);
        }
    }

    removeJobsListItem(id, dislikeId = false, companyName = false, companyId = false) {
        if (this.insideListing(id, companyName)) {
            let hideHeadline = true;
            for (let i = 0; i < this.jobsListItems.length; i += 1) {
                if ((companyName && this.jobsListItems[i].props.companyId === id)
                    || (!companyName && this.jobsListItems[i].props.id === id)) {
                    this.dom.jobsListItems[i].parentNode.classList.add('m-jobsList__item--hidden');
                    setTimeout(() => {
                        this.dom.jobsListItems[i].parentNode.classList.add('m-jobsList__item--displayNone');
                    }, 1000);
                } else if (!this.dom.jobsListItems[i].parentNode.classList.contains('m-jobsList__item--hidden')) {
                    hideHeadline = false;
                }
            }
            if (hideHeadline) {
                if (this.inactiveJobsListHeader) {
                    this.inactiveJobsListHeader.hideHeadline();
                }
                this.events.emit('hideHeadline');
            }

            this.events.emit('removedJobItem', id, dislikeId, companyName, companyId);
        }
    }

    addJobsListItem(id, companyName = false, ignoreJobIds = []) {
        if (this.insideListing(id, companyName)) {
            for (let i = 0; i < this.jobsListItems.length; i += 1) {
                if (companyName) {
                    if (this.jobsListItems[i].props.companyId === id) {
                        if (!ignoreJobIds.includes(parseInt(this.jobsListItems[i].props.id, 10))) {
                            this.dom.jobsListItems[i].parentNode.classList.remove('m-jobsList__item--displayNone');
                            setTimeout(() => {
                                this.dom.jobsListItems[i].parentNode.classList.remove('m-jobsList__item--hidden');
                            }, 20);
                        }
                    }
                } else if (this.jobsListItems[i].props.id === id) {
                    this.dom.jobsListItems[i].parentNode.classList.remove('m-jobsList__item--displayNone');
                    setTimeout(() => {
                        this.dom.jobsListItems[i].parentNode.classList.remove('m-jobsList__item--hidden');
                    }, 20);
                }
            }
            if (this.inactiveJobsListHeader) {
                this.inactiveJobsListHeader.showHeadline();
            }
            this.events.emit('showHeadline');
        }
    }

    updateShowMemoButtonStates(showMemoButton, jobId) {
        const item = this.jobsListItems?.find((jobsListItem) => jobsListItem.props.id === jobId);
        item?.updateShowMemoButtonState(showMemoButton);
    }

    appendItemsToList(items) {
        // add a11y screenreader only div
        const screenReaderOnlyDiv = document.createElement('div');
        screenReaderOnlyDiv.tabIndex = 0;
        this.dom.el.appendChild(screenReaderOnlyDiv);
        screenReaderOnlyDiv.focus();
        screenReaderOnlyDiv.remove();
        // iterate through every item we got from the loadMoreJobs xhr
        items.forEach((item) => {
            Object.keys(item).forEach((key) => {
                // create listElement
                const listItem = document.createElement('li');
                const div = document.createElement('div');
                listItem.className = 'm-jobsList__item';
                div.className = key;
                // append listElement to jobsList
                listItem.appendChild(div);
                this.dom.el.appendChild(listItem);

                // check if item is a jobsItem or bsAd
                if (key === 'jobsItem') {
                    // add ga tracking properties to li
                    listItem.setAttribute('data-gtm-item-list', 'jobsearch');
                    listItem.setAttribute('data-gtm-item-type', 'job');
                    listItem.setAttribute('data-gtm-item-id', item[key].id);
                    // create new js module instance for item
                    const module = new JobsListItem(div);
                    this.jobsListItems.push(module);
                    // render item with properties from the xhr
                    module.setProps(item[key], true);
                    // add all needed events to the new rendered item
                    this.propagateEvents(module, 'jobClick');
                    this.propagateEvents(module, 'openMemoTab');

                    module.events.on('jobClick', () => {
                        this.onJobClick(module.dom.el);
                    });
                    module.events.on('openMemoTab', () => {
                        this.onOpenMemoTab(module.dom.el);
                    });
                } else if (key === 'bsAd') {
                    listItem.classList.add('m-jobsList__bsAdContainer');
                    // create new js module instance for item
                    this.loadedBsAd = new BrandingSolutionAdCard(div);
                    // render item with properties from the xhr
                    this.loadedBsAd.setProps(item[key], true);
                }
            });
        });
        this.dom.jobsListItems = this.dom.el.querySelectorAll('.m-jobsListItem');
    }

    onJobClick(item) {
        if (!mediaQueries.isMobile()) {
            this.removeHighlightedJobItems();
            item.classList.add('m-jobsListItem--highlighted');
        }
    }

    onOpenMemoTab(item) {
        if (!mediaQueries.isMobile()) {
            this.removeHighlightedJobItems();
            item.classList.add('m-jobsListItem--highlighted');
        }
    }
}
